// MasterJettyAddEdit.jsx
'use client';

import { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  TextField,
  CircularProgress,
  Button,
  FormControlLabel,
  Switch,
  InputAdornment,
  Typography,
  IconButton
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import MapIcon from '@mui/icons-material/Map';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import usePickerPagination from '../../../hooks/usePickerPagination';

import DrawPolygon from '../../../components/DrawPolygon';
import PickerField from '../../../components/PickerField';
import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';

import { createTransitPoint, updateTransitPoint, getTransitPointById } from '../../../stores/transitPointStore';
import {
  featureToFeatureCollection,
  geojsonToWKT,
  polygonCoordsStringFromGeoJSON,
  computeCentroidFromGeoJSON,
  wktToGeoJSON
} from '../../../utils/geo';

import { useCompanyDataList } from '../../../stores/companyStore';
import { useCustomerList } from '../../../stores/customerStore';
import MasterCustomerAddEdit from '../customer/MasterCustomerAddEdit';
import MasterCompanyAddEdit from '../company/MasterCompanyAddEdit';
import { formatUpperCase } from '../../../utils/formatTitleCase';
import { useLanguage } from '../../../utils/i18n';

const MasterJettyAddEdit = ({ open, id = null, onClose, onSuccess, passedWkt = null, disabledViewMap = false }) => {
  const { t } = useLanguage();
  const notify = useNotifier();
  const [mapOpen, setMapOpen] = useState(false);
  const [isEditingGeom, setIsEditingGeom] = useState(false);
  const [initialValues, setInitialValues] = useState({
    company: null,
    company_name: '',
    name: '',
    code: '',
    transit_point_type: '',
    customer: [],
    customer_name: '',
    trip_group_transit_point: '',
    trip_group_transit_point_name: '',
    phone: '',
    mobile_no: '',
    fax: '',
    pic_name: '',
    pic_phone: '',
    pic_mobile: '',
    address: '',
    remark: '',
    zip_code: '',
    status: true,
    geom: null,
    geom_wkb: null,
    coords: null,
    centroid_lat: null,
    centroid_lon: null,
    info: null,
    latitude: null,
    longitude: null
  });

  const [loading, setLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [initialCustomerLabels, setInitialCustomerLabels] = useState({});

  const normalizeTransitTypeToBackend = (v) => {
    if (v == null) return '';
    const s = String(v).trim().toLowerCase();
    if (s === 'loading' || s === 'unloading') return s;
    return s;
  };

  const normalizeCustomerIds = (raw) => {
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw.map((x) => (x == null ? '' : String(x))).filter(Boolean);
    if (typeof raw === 'string') {
      if (raw.includes(','))
        return raw
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      return raw === '' ? [] : [raw];
    }
    if (typeof raw === 'number') return [String(raw)];
    if (typeof raw === 'object' && (raw.id || raw.value)) return [String(raw.id ?? raw.value)];
    return [];
  };

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const companyOptions = companyPickerParams.options;

  const customerPickerParams = usePickerPagination({
    fetchHook: useCustomerList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue, state.companyId],
    searchKey: 'name',
    companyId: selectedCompanyId
  });

  // 1. AUTO-FILL COMPANY DARI LOCAL STORAGE (Saat mode Add)
  useEffect(() => {
    if (id || !open) return;

    const storedCompanyId = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
    if (!storedCompanyId) return;

    if (companyOptions && companyOptions.length > 0) {
      const match = companyOptions.find((c) => String(c.value) === String(storedCompanyId));
      if (match) {
        setSelectedCompanyId(String(match.value));
        setInitialValues((prev) => ({
          ...prev,
          company: String(match.value),
          company_name: match.label ?? match.name ?? String(match.value)
        }));
        return;
      }
    }

    // Fallback jika belum ada di options (sedang loading hook)
    setSelectedCompanyId(String(storedCompanyId));
    setInitialValues((prev) => ({
      ...prev,
      company: String(storedCompanyId),
      company_name: String(storedCompanyId)
    }));
  }, [companyOptions, id, open]);

  // 2. FETCH DATA UNTUK MODE EDIT & HANDLE WKT DARI MAP
  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetch = async () => {
      if (!id) {
        setLoading(false);
        if (passedWkt) {
          const geojsonFeature = wktToGeoJSON(passedWkt);
          if (geojsonFeature) {
            const fc = featureToFeatureCollection(geojsonFeature);
            const centroid = computeCentroidFromGeoJSON(geojsonFeature);
            const coordsStr = polygonCoordsStringFromGeoJSON(geojsonFeature);

            setInitialValues((prev) => ({
              ...prev,
              geom: passedWkt,
              info: fc,
              coords: coordsStr,
              centroid_lat: centroid.lat,
              centroid_lon: centroid.lon,
              latitude: centroid.lat,
              longitude: centroid.lon
            }));
          } else {
            setInitialValues((prev) => ({ ...prev, geom: passedWkt }));
          }
        } else {
          // Reset value (kecuali company jika diset oleh localstorage)
          setInitialCustomerLabels({});
          setInitialValues((prev) => ({
            company: prev.company,
            company_name: prev.company_name,
            name: '',
            code: '',
            transit_point_type: '',
            customer: [],
            customer_name: '',
            trip_group_transit_point: '',
            trip_group_transit_point_name: '',
            phone: '',
            mobile_no: '',
            fax: '',
            pic_name: '',
            pic_phone: '',
            pic_mobile: '',
            address: '',
            remark: '',
            zip_code: '',
            status: true,
            min_speed: '',
            max_speed: '',
            geom: null,
            geom_wkb: null,
            coords: null,
            centroid_lat: null,
            centroid_lon: null,
            info: null,
            latitude: null,
            longitude: null
          }));
        }
        return;
      }

      setLoading(true);
      try {
        const data = await getTransitPointById(id);
        if (!mounted) return;

        if (data) {
          const companyVal = data.company ?? data.company_id ?? null;

          if (companyVal != null) setSelectedCompanyId(String(companyVal));

          const rawCustomerCandidates =
            data.customer_ids ?? data.customer_id ?? data.customer ?? data.join?.customer_ids ?? data.join?.customer_id ?? null;
          const customerArr = normalizeCustomerIds(rawCustomerCandidates);

          const transitTypeValRaw = data.transit_point_type ?? '';
          const transitTypeNormalized = normalizeTransitTypeToBackend(transitTypeValRaw);

          const custNameStr = data.customer_name ?? data.join?.customer_name ?? initialValues.customer_name ?? '';
          const namesArr = custNameStr ? String(custNameStr).split(', ') : [];
          const labelMap = {};
          customerArr.forEach((cid, idx) => {
            if (namesArr[idx]) {
              labelMap[String(cid)] = namesArr[idx];
            }
          });
          setInitialCustomerLabels(labelMap);

          const cleanStr = (val) => (val === '-' ? '' : val);

          setInitialValues((prev) => ({
            ...prev,
            company: companyVal != null ? String(companyVal) : prev.company,
            company_name: data.company_name ?? data.join?.company_name ?? prev.company_name,
            name: cleanStr(data.name) ?? prev.name,
            code: cleanStr(data.code) ?? prev.code,
            transit_point_type: transitTypeNormalized || '',
            customer: customerArr,
            customer_name: cleanStr(custNameStr),
            phone: cleanStr(data.phone) ?? prev.phone,
            mobile_no: cleanStr(data.mobile_no) ?? prev.mobile_no,
            fax: cleanStr(data.fax) ?? prev.fax,
            pic_name: cleanStr(data.pic_name) ?? prev.pic_name,
            pic_phone: cleanStr(data.pic_phone) ?? prev.pic_phone,
            pic_mobile: cleanStr(data.pic_mobile) ?? prev.pic_mobile,
            address: cleanStr(data.address) ?? prev.address,
            remark: cleanStr(data.remark) ?? prev.remark,
            zip_code: cleanStr(data.zip_code) ?? prev.zip_code,
            status: typeof data.status === 'boolean' ? data.status : prev.status,
            min_speed: data.min_speed ?? prev.min_speed,
            max_speed: data.max_speed ?? prev.max_speed,
            geom: cleanStr(data.geom) ?? null,
            geom_wkb: cleanStr(data.geom_wkb) ?? null,
            coords: cleanStr(data.coords) ?? null,
            centroid_lat: cleanStr(data.centroid_lat) ?? null,
            centroid_lon: cleanStr(data.centroid_lon) ?? null,
            info: cleanStr(data.info) ?? null,
            latitude: cleanStr(data.latitude) ?? cleanStr(data.centroid_lat) ?? prev.latitude,
            longitude: cleanStr(data.longitude) ?? cleanStr(data.centroid_lon) ?? prev.longitude
          }));
        }
      } catch (err) {
        console.error('Failed to load transit point:', err);
        notify({ message: 'Gagal memuat data Jetty', severity: 'error' }); // Menambahkan notifikasi error
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, open, passedWkt, notify]);

  const validationSchema = Yup.object().shape({
    company: Yup.mixed().required(t('geofenceList.validationCompanyRequired', 'Company is required')),
    name: Yup.string().required(t('validation.nameRequired', 'Name is required')),
    code: Yup.mixed().required(t('validation.codeRequired', 'Code is required')),
    address: Yup.string().required(t('validation.addressRequired', 'Address is required'))
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (values.company == null || values.company === '') throw new Error('Company id tidak valid');

        const numericOrNull = (v) => {
          if (v == null || v === '') return null;
          const n = Number(v);
          return Number.isNaN(n) ? v : n;
        };

        const custIds = Array.isArray(values.customer)
          ? values.customer.map(numericOrNull).filter(Boolean)
          : values.customer
            ? [numericOrNull(values.customer)].filter(Boolean)
            : [];
        const firstCustomer = custIds.length ? custIds[0] : 0;
        const codeUpper = values.code;
        const nameUpper = formatUpperCase(values.name);

        const payload = {
          address: values.address || '-',
          blacklist: false,
          centroid_lat: values.centroid_lat ?? 0,
          centroid_lon: values.centroid_lon ?? 0,
          code: codeUpper || '-',
          company: values.company != null ? Number(values.company) : 0,
          coords: values.coords ?? 0,
          customer: firstCustomer ?? 0,
          customer_ids: custIds,
          fax: values.fax || '-',
          geocode: values.geocode || '-',
          geom: values.geom || '-',
          geom_wkb: values.geom_wkb || '-',
          id: id ?? 0,
          image: values.image || '-',
          latitude: values.latitude ?? values.centroid_lat ?? 0,
          longitude: values.longitude ?? values.centroid_lon ?? 0,
          mobile_no: values.mobile_no || '-',
          name: nameUpper || '-',
          phone: values.phone || '-',
          pic_mobile: values.pic_mobile || '-',
          pic_name: values.pic_name || '-',
          pic_phone: values.pic_phone || '-',
          register_date: values.register_date || '',
          remark: values.remark || '-',
          status: Boolean(values.status),
          min_speed: values.min_speed ? Number(values.min_speed) : 0,
          max_speed: values.max_speed ? Number(values.max_speed) : 0,
          transit_point_type: 'unloading',
          trip_group_transit_point: null,
          unregister_date: values.unregister_date || '',
          zip_code: values.zip_code || '-'
        };
        let response;

        if (id) {
          response = await updateTransitPoint(id, payload);
        } else {
          response = await createTransitPoint(payload);
        }
        if (response?.code === 400 || response?.error === true || response?.error) {
          throw response;
        }

        notify({
          message: id ? t('notify.update', 'Updated successfully') : t('notify.create', 'Created successfully'),
          severity: 'success'
        });

        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage =
          err?.response?.data?.message || err?.data?.message || err?.message || t('notify.failedSave', 'Failed to save data');
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  // Force reset formik state and initial labels when dialog state changes
  // This prevents unsaved values (like selected customers) from bleeding over
  // into the next Add/Edit session if Formik's enableReinitialize fails to trigger.
  useEffect(() => {
    if (open) {
      formik.resetForm();
      if (!id) {
        setInitialCustomerLabels({});
      }
    } else {
      formik.resetForm();
      setInitialCustomerLabels({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  const getOptionId = (o) => (o == null ? o : (o.value ?? o.id ?? o));
  const getOptionLabel = (o) => (o == null ? '' : (o.label ?? o.name ?? String(o.value ?? o.id ?? o)));

  const dialogActions = (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{
          backgroundColor: '#0072DA',
          textTransform: 'none',
          borderRadius: 1,
          px: 3,
          fontWeight: 700,
          boxShadow: '0 8px 20px rgba(25,118,210,0.12)'
        }}
      >
        {formik.isSubmitting ? 'Processing...' : 'Save'}
      </Button>
      <Button onClick={onClose} disabled={formik.isSubmitting} variant="outlined">
        Cancel
      </Button>
    </>
  );

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={id ? t('masterJetty.edit', 'Edit Jetty') : t('masterJetty.add', 'Add Jetty')}
      fullScreenMode={true}
      fullHeight={true}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', maxWidth: 'none', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: 'flex', gap: 4, alignItems: 'stretch', width: '100%', flexDirection: { xs: 'column', md: 'row' } }}>
              {/* Kolom Kiri */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Stack spacing={3}>
                  <PickerField
                    label="Company *"
                    value={formik.values.company}
                    {...companyPickerParams}
                    options={companyOptions}
                    onChange={(opt) => {
                      const val = typeof opt === 'object' ? opt?.value : opt;
                      const selected = companyOptions.find((c) => String(c.value) === String(val)) ?? null;
                      const idVal = selected ? selected.value : (val ?? null);
                      formik.setFieldValue('company', idVal);
                      if (selected) formik.setFieldValue('company_name', selected.label);
                      setSelectedCompanyId(idVal || '');

                      formik.setFieldValue('customer', []);
                      formik.setFieldValue('customer_name', '');
                      formik.setFieldValue('trip_group_transit_point', '');
                      formik.setFieldValue('trip_group_transit_point_name', '');
                    }}
                    dialogTitle={t('general.selectCompany', 'Select Company')}
                    allowAdd
                    AddComponent={MasterCompanyAddEdit}
                    error={formik.touched.company && !!formik.errors.company}
                    helperText={formik.touched.company && formik.errors.company}
                  />

                  <TextField
                    label={`${t('masterJetty.name', 'Jetty Name')} *`}
                    name="name"
                    value={formik.values.name ?? ''}
                    onChange={(e) => {
                      formik.setFieldValue('name', formatUpperCase(e.target.value));
                    }}
                    onBlur={formik.handleBlur}
                    size="medium"
                    fullWidth
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                  />

                  <TextField
                    label={t('masterJetty.code', 'Jetty Code')}
                    name="code"
                    value={formik.values.code ?? ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    size="medium"
                    fullWidth
                  />

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label={t('masterJetty.minSpeedInArea', 'Min Speed In Area')}
                      name="min_speed"
                      type="number"
                      value={formik.values.min_speed ?? ''}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="medium"
                      fullWidth
                      error={formik.touched.min_speed && Boolean(formik.errors.min_speed)}
                      helperText={formik.touched.min_speed && formik.errors.min_speed}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">KM/H</InputAdornment>
                      }}
                    />
                    <TextField
                      label={t('masterJetty.maxSpeedInArea', 'Max Speed In Area')}
                      name="max_speed"
                      type="number"
                      value={formik.values.max_speed ?? ''}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="medium"
                      fullWidth
                      error={formik.touched.max_speed && Boolean(formik.errors.max_speed)}
                      helperText={formik.touched.max_speed && formik.errors.max_speed}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">KM/H</InputAdornment>
                      }}
                    />
                  </Box>

                  <PickerField
                    label={t('masterJetty.customer', 'Customer')}
                    value={formik.values.customer}
                    selectedItems={
                      Object.keys(initialCustomerLabels).length > 0
                        ? Object.keys(initialCustomerLabels).map((id) => ({
                            value: id,
                            label: initialCustomerLabels[id]
                          }))
                        : []
                    }
                    disabled={!selectedCompanyId}
                    multiSelect={true}
                    {...customerPickerParams}
                    onChange={(opt) => {
                      // opt is now an array of IDs
                      formik.setFieldValue('customer', opt || []);
                    }}
                    allowAdd
                    AddComponent={MasterCustomerAddEdit}
                    getOptionId={(o) => getOptionId(o)}
                    getOptionLabel={(o) => getOptionLabel(o)}
                    dialogTitle={t('masterCustomer.title', 'Master Customer')}
                    error={formik.touched.customer && !!formik.errors.customer}
                    helperText={
                      !formik.values.company
                        ? t('general.selectCompanyFirst', '* Please select a company first')
                        : formik.touched.customer && formik.errors.customer
                    }
                  />

                  <TextField
                    label={t('masterJetty.phone', 'Phone')}
                    name="phone"
                    value={formik.values.phone ?? ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    size="medium"
                    fullWidth
                  />
                  <TextField
                    label={t('masterJetty.mobile', 'Mobile')}
                    name="mobile_no"
                    value={formik.values.mobile_no ?? ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    size="medium"
                    fullWidth
                  />
                  <TextField
                    label={t('masterJetty.fax', 'Fax')}
                    name="fax"
                    value={formik.values.fax ?? ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    size="medium"
                    fullWidth
                  />
                </Stack>
              </Box>

              {/* Kolom Kanan */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Stack spacing={3}>
                  <TextField
                    label={t('masterJetty.picName', 'PIC Name')}
                    name="pic_name"
                    value={formik.values.pic_name ?? ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    size="medium"
                    fullWidth
                  />
                  <TextField
                    label={t('masterJetty.picPhone', 'PIC Phone')}
                    name="pic_phone"
                    value={formik.values.pic_phone ?? ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    size="medium"
                    fullWidth
                  />
                  <TextField
                    label={t('masterJetty.picMobile', 'PIC Mobile')}
                    name="pic_mobile"
                    value={formik.values.pic_mobile ?? ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    size="medium"
                    fullWidth
                  />
                  <TextField
                    label={`${t('masterJetty.address', 'Address')} *`}
                    name="address"
                    value={formik.values.address ?? ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    size="medium"
                    fullWidth
                    multiline
                    rows={2}
                    error={formik.touched.address && Boolean(formik.errors.address)}
                    helperText={formik.touched.address && formik.errors.address}
                  />
                  <TextField
                    label={t('masterJetty.remark', 'Remark')}
                    name="remark"
                    value={formik.values.remark ?? ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    size="medium"
                    fullWidth
                  />
                  <TextField
                    label={t('masterJetty.zipCode', 'Zip Code')}
                    name="zip_code"
                    value={formik.values.zip_code ?? ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    size="medium"
                    fullWidth
                  />

                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(formik.values.status)}
                          onChange={(e) => formik.setFieldValue('status', e.target.checked)}
                          name="status"
                        />
                      }
                      label={t('masterJetty.status', 'Status')}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      label={t('masterJetty.drawLocation', 'Draw Location (WKT)')}
                      name="geom"
                      value={formik.values.geom ?? ''}
                      onChange={(e) => {
                        const formatted = e.target.value.toUpperCase().replace(/[^A-Z0-9\s().,-]/g, '');
                        formik.setFieldValue('geom', formatted);
                      }}
                      onBlur={formik.handleBlur}
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={5}
                      InputProps={{
                        readOnly: !isEditingGeom,
                        sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setIsEditingGeom(!isEditingGeom)}
                              edge="end"
                              title={isEditingGeom ? 'Simpan' : 'Edit Manual'}
                              sx={{
                                bgcolor: isEditingGeom ? '#e8f5e9' : '#e3f2fd',
                                color: isEditingGeom ? '#2e7d32' : '#1976d2',
                                borderRadius: '10px',
                                p: 0.8,
                                boxShadow: isEditingGeom ? '0 2px 6px rgba(46, 125, 50, 0.2)' : '0 2px 6px rgba(25, 118, 210, 0.2)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  bgcolor: isEditingGeom ? '#c8e6c9' : '#bbdefb',
                                  transform: 'translateY(-2px)',
                                  boxShadow: isEditingGeom ? '0 4px 10px rgba(46, 125, 50, 0.3)' : '0 4px 10px rgba(25, 118, 210, 0.3)'
                                }
                              }}
                            >
                              {isEditingGeom ? <CheckIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      helperText={
                        formik.values.info ? `Saved ${formik.values.info.features?.length ?? 1} feature(s)` : 'There are no polygon yet'
                      }
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button variant="outlined" disabled={disabledViewMap} startIcon={<MapIcon />} onClick={() => setMapOpen(true)}>
                      {t('masterJetty.viewMap', 'View Map')}
                    </Button>
                    {disabledViewMap && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        *To change the polygon, click cancel.
                      </Typography>
                    )}
                  </Box>
                  {mapOpen && (
                    <DrawPolygon
                      open={mapOpen}
                      onClose={() => setMapOpen(false)}
                      title={t('masterJetty.drawLocation', 'Draw Polygon')}
                      initialGeoJSON={formik.values.info}
                      center={
                        formik.values.centroid_lat && formik.values.centroid_lon
                          ? [formik.values.centroid_lat, formik.values.centroid_lon]
                          : undefined
                      }
                      zoom={formik.values.centroid_lat && formik.values.centroid_lon ? 15 : undefined}
                      position="topleft"
                      singleFeature={true}
                      onSave={(geojson) => {
                        if (!geojson) {
                          formik.setFieldValue('info', null);
                          formik.setFieldValue('geom', null);
                          formik.setFieldValue('coords', null);
                          formik.setFieldValue('centroid_lat', null);
                          formik.setFieldValue('centroid_lon', null);
                          formik.setFieldValue('latitude', null);
                          formik.setFieldValue('longitude', null);
                          return;
                        }
                        const fc = featureToFeatureCollection(geojson);
                        const first = fc.features?.[0] ?? null;
                        const wkt = geojsonToWKT(first ?? fc);
                        const coordsStr = polygonCoordsStringFromGeoJSON(first ?? fc);
                        const centroid = computeCentroidFromGeoJSON(first ?? fc);

                        formik.setFieldValue('info', fc);
                        formik.setFieldValue('geom', wkt);
                        formik.setFieldValue('coords', coordsStr);
                        formik.setFieldValue('centroid_lat', centroid.lat);
                        formik.setFieldValue('centroid_lon', centroid.lon);
                        formik.setFieldValue('latitude', centroid.lat);
                        formik.setFieldValue('longitude', centroid.lon);
                      }}
                    />
                  )}
                </Stack>
              </Box>
            </Box>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default MasterJettyAddEdit;
